import { isSome } from '@mbfab/shared'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Download,
  Maximize,
  Minus,
  Plus,
  Printer,
  Ruler,
  Settings2,
  X,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useId, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogPortal, DialogPrimitive } from '@/components/ui/dialog'
import { CompactFormField, CompactFormInput, CompactFormTextarea } from '@/components/ui/form'
import { useResize } from '@/lib/use-resize'
import { cn } from '@/lib/utils'

/** 帳票設定とプレビューを並べる、Mill-Boxの印刷フロー専用の全画面2ペインダイアログ。 */
export function PrintDialogShell({
  title = '印刷・データ出力',
  description,
  onClose,
  children,
}: {
  title?: string
  description: ReactNode
  onClose: () => void
  children: ReactNode
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const titleId = useId()

  return (
    <div ref={rootRef}>
      <Dialog
        open
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            onClose()
          }
        }}
      >
        <DialogPortal container={rootRef}>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/45 p-4 backdrop-blur-[2px] lg:p-6">
            <DialogPrimitive.Popup
              aria-labelledby={titleId}
              initialFocus={false}
              className="flex h-[calc(100vh-2rem)] w-full max-w-[1460px] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl lg:h-[calc(100vh-3rem)]"
            >
              <header className="flex shrink-0 items-center justify-between border-b border-border px-5 py-4 lg:px-6">
                <div className="flex items-center gap-3">
                  <span className="flex size-9 items-center justify-center rounded-lg bg-accent text-primary">
                    <Printer className="size-5" />
                  </span>
                  <div>
                    <DialogPrimitive.Title id={titleId} render={<h2 />} className="text-lg font-bold text-foreground">
                      {title}
                    </DialogPrimitive.Title>
                    <p className="text-xs text-muted-foreground">{description}</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose} aria-label="閉じる">
                  <X className="size-5" />
                </Button>
              </header>
              <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(390px,0.82fr)_minmax(520px,1.18fr)]">
                {children}
              </div>
            </DialogPrimitive.Popup>
          </div>
        </DialogPortal>
      </Dialog>
    </div>
  )
}

/**
 * 印刷ダイアログのタブ。「印刷（プレビューを見て刷る）」と「データ出力（元データを落とす）」は
 * 目的も出力先も違う別の仕事なので、1枚の設定ペインに縦積みせずタブで分ける
 * （2026/08/30 「ミルシート/加工証明のダウンロードUIが発注照合と違う」FBで全画面に統一）。
 */
export type PrintDialogTab = 'print' | 'export'

export function PrintDialogTabNav({
  tab,
  onChangeTab,
}: {
  tab: PrintDialogTab
  onChangeTab: (tab: PrintDialogTab) => void
}) {
  return (
    <div className="shrink-0 border-b border-border px-5 pt-3 lg:px-6">
      <div className="flex gap-5" role="tablist" aria-label="出力方法">
        <PrintDialogTabButton
          active={tab === 'print'}
          onClick={() => {
            onChangeTab('print')
          }}
        >
          印刷設定
        </PrintDialogTabButton>
        <PrintDialogTabButton
          active={tab === 'export'}
          onClick={() => {
            onChangeTab('export')
          }}
        >
          データ出力
        </PrintDialogTabButton>
      </div>
    </div>
  )
}

function PrintDialogTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'border-b-2 px-1 py-3 text-sm font-semibold transition-colors',
        active ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}

/** 「データ出力」タブの本文。見出し・スクロール領域を全画面で揃えるための器。 */
export function PrintExportBody({ description, children }: { description?: ReactNode; children: ReactNode }) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 lg:px-6">
      <div className="flex flex-col gap-5">
        <div>
          <h3 className="text-sm font-semibold text-foreground">データをダウンロード</h3>
          {isSome(description) && <p className="mt-1 text-xs leading-5 text-muted-foreground">{description}</p>}
        </div>
        {children}
      </div>
    </div>
  )
}

/** Excel / CSV は帳票と違って画面上のプレビューを提供しない。 */
export function PrintExportPreviewPane() {
  return (
    <PrintDialogPreviewPane>
      <div className="flex flex-1 items-center justify-center p-8 text-center text-sm text-muted-foreground">
        Excel、CSVはプレビューできません。
      </div>
    </PrintDialogPreviewPane>
  )
}

/** ダウンロードできるファイル1種類を表すカード。 */
export function PrintExportCard({
  icon,
  title,
  format,
  description,
  disabled,
  testId,
  onDownload,
  details,
  detailsOpen,
  onToggleDetails,
  disabledReason,
}: {
  icon: ReactNode
  title: string
  format: string
  description: string
  disabled: boolean
  testId?: string
  onDownload: () => Promise<void>
  details?: ReactNode
  detailsOpen?: boolean
  onToggleDetails?: () => void
  disabledReason?: string
}) {
  return (
    <article className="rounded-lg border border-border p-4">
      <div className="flex items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-secondary text-foreground">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-semibold text-foreground">{title}</h4>
          <p className="mt-0.5 text-xs font-medium text-primary">{format}</p>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">{description}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <span title={disabled ? disabledReason : undefined}>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={disabled}
                data-testid={testId}
                onClick={() => {
                  void onDownload()
                }}
              >
                <Download className="size-3.5" />
                ダウンロード
              </Button>
            </span>
            {isSome(details) && isSome(onToggleDetails) && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5"
                aria-expanded={detailsOpen ?? false}
                onClick={onToggleDetails}
              >
                <Settings2 className="size-3.5" />
                詳細設定
                <ChevronDown className={cn('size-3.5 transition-transform', detailsOpen === true && 'rotate-180')} />
              </Button>
            )}
          </div>
        </div>
      </div>
      {isSome(details) && detailsOpen === true && <div className="mt-4 border-t border-border pt-4">{details}</div>}
    </article>
  )
}

export function PrintDialogStatusPanes({
  status,
  loadingTestId,
  errorTestId,
  settingsHeader,
  loadingMessage = '印刷データを読み込み中です…',
  errorMessage = '印刷データの取得に失敗しました。ダイアログを閉じてもう一度お試しください。',
}: {
  status: 'loading' | 'error'
  loadingTestId: string
  errorTestId: string
  settingsHeader?: ReactNode
  loadingMessage?: ReactNode
  errorMessage?: ReactNode
}) {
  return (
    <>
      <div className="flex min-h-0 flex-col border-b border-border bg-background lg:border-r lg:border-b-0">
        {settingsHeader}
        <div
          className={cn(
            'flex min-h-0 flex-1 items-center justify-center gap-2 p-8 text-sm',
            status === 'error' && 'flex-col text-center text-destructive',
            status === 'loading' && 'text-muted-foreground',
          )}
          data-testid={status === 'loading' ? loadingTestId : errorTestId}
        >
          {status === 'loading' ? <Printer className="size-4 animate-pulse" /> : <AlertTriangle className="size-5" />}
          {status === 'loading' ? loadingMessage : errorMessage}
        </div>
      </div>
      <PrintDialogPreviewPane />
    </>
  )
}

export function PrintDialogSettingsPane({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-0 flex-col border-b border-border bg-background lg:border-r lg:border-b-0">
      {children}
    </div>
  )
}

export function PrintDialogSettingsBody({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 lg:px-6">
      <div className="flex flex-col gap-6">{children}</div>
    </div>
  )
}

export function PrintDialogFooter({ summary, children }: { summary: ReactNode; children: ReactNode }) {
  return (
    <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-border px-5 py-3.5 lg:px-6">
      <span className="text-xs text-muted-foreground">{summary}</span>
      {children}
    </footer>
  )
}

export function PrintDialogPreviewPane({ children }: { children?: ReactNode }) {
  return (
    <aside className="flex min-h-[420px] min-w-0 flex-col bg-muted" aria-label="印刷プレビュー">
      {children}
    </aside>
  )
}

export function PrintDialogPreviewHeader({ pageCount, format = 'A4・縦' }: { pageCount: number; format?: string }) {
  return (
    <div className="flex h-12 shrink-0 items-center justify-between border-b border-border px-4">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-foreground">プレビュー</span>
        <span className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground">
          {pageCount}ページ
        </span>
      </div>
      <span className="text-xs text-muted-foreground">{format}</span>
    </div>
  )
}

// A4 縦（210mm × 295.5mm）を CSS ピクセルに直した寸法。プレビューを枠に収める倍率の計算に使う
const MM_TO_PX = 96 / 25.4
const A4_WIDTH_PX = 210 * MM_TO_PX
const A4_HEIGHT_PX = 295.5 * MM_TO_PX

/**
 * 印刷プレビューの表示倍率。既定は枠に収まる倍率（「全体に合わせる」）で、
 * 利用者が拡大・縮小・実寸を押したときだけその倍率を優先する。
 * 3画面（発注照合・ミルシート・加工証明）で同じ操作感にするため、ここに1つだけ置いて共有する。
 */
export function usePrintPreviewZoom() {
  const viewport = useResize()
  const [manualZoom, setManualZoom] = useState<number | null>(null)

  const fitZoom =
    isSome(viewport.rect) && viewport.rect.height > 0
      ? Math.min((viewport.rect.height - 16) / A4_HEIGHT_PX, (viewport.rect.width - 16) / A4_WIDTH_PX, 1)
      : 0.5
  const zoom = manualZoom ?? Math.max(fitZoom, 0.1)

  return {
    /** プレビューのスクロール領域に渡す ref。この要素の大きさから「全体に合わせる」倍率を決める */
    viewportRef: viewport.ref,
    zoom,
    zoomIn: () => {
      setManualZoom(Math.min(2, zoom + 0.25))
    },
    zoomOut: () => {
      setManualZoom(Math.max(0.25, zoom - 0.25))
    },
    fitToViewport: () => {
      setManualZoom(null)
    },
    actualSize: () => {
      setManualZoom(1)
    },
  }
}

/** プレビュー右ペインの上部バー（ページ数 + 倍率操作）。 */
export function PrintDialogPreviewToolbar({
  pageCount,
  zoom,
}: {
  pageCount: number
  zoom: Pick<ReturnType<typeof usePrintPreviewZoom>, 'zoomIn' | 'zoomOut' | 'fitToViewport' | 'actualSize'>
}) {
  return (
    <div className="flex items-center justify-between border-b border-border bg-background px-4 py-2">
      <PrintDialogPreviewHeader pageCount={pageCount} />
      <div className="flex items-center gap-1" aria-label="プレビュー倍率">
        <Button variant="outline" size="icon" aria-label="縮小" onClick={zoom.zoomOut}>
          <Minus className="size-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={zoom.fitToViewport}>
          <Maximize data-icon="inline-start" />
          全体に合わせる
        </Button>
        <Button variant="outline" size="sm" onClick={zoom.actualSize}>
          <Ruler data-icon="inline-start" />
          実寸
        </Button>
        <Button variant="outline" size="icon" aria-label="拡大" onClick={zoom.zoomIn}>
          <Plus className="size-4" />
        </Button>
      </div>
    </div>
  )
}

export function PrintTargetRow({
  index,
  checked,
  onChange,
  label,
  description,
  details,
}: {
  index: number
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  description: string
  details?: ReactNode
}) {
  return (
    <div className={cn('px-3.5 py-3', index > 0 && 'border-t border-border')}>
      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => {
            onChange(event.target.checked)
          }}
          className="sr-only"
        />
        <PrintCheckBox checked={checked} />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-foreground">{label}</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">{description}</span>
        </span>
      </label>
      {isSome(details) && <div className="mt-3 pl-7.5">{details}</div>}
    </div>
  )
}

/** 印刷対象カード内で、対象固有の設定を開閉する共通トグル。 */
export function PrintDetailsSection({
  open,
  onToggleOpen,
  children,
  testId,
}: {
  open: boolean
  onToggleOpen: () => void
  children: ReactNode
  testId?: string
}) {
  return (
    <div className="rounded-md bg-secondary/60">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-medium text-foreground"
        aria-expanded={open}
        data-testid={testId}
        onClick={onToggleOpen}
      >
        詳細表示設定
        <ChevronDown className={cn('size-3.5 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>
      {open && <div className="flex flex-col gap-4 border-t border-border px-3 py-3">{children}</div>}
    </div>
  )
}

export function PrintCheckBox({ checked, small = false }: { checked: boolean; small?: boolean }) {
  return (
    <span
      className={cn(
        'mt-0.5 flex shrink-0 items-center justify-center rounded border',
        small ? 'size-4' : 'size-4.5',
        checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-card',
      )}
    >
      {checked && <Check className="size-3" strokeWidth={3} />}
    </span>
  )
}

export function PrintDialogField({
  label,
  value,
  placeholder,
  multiline = false,
  onChange,
}: {
  label: string
  value: string
  placeholder?: string
  multiline?: boolean
  onChange: (value: string) => void
}) {
  const id = useId()
  return (
    <CompactFormField id={id} label={label}>
      {multiline ? (
        <CompactFormTextarea
          id={id}
          rows={2}
          value={value}
          placeholder={placeholder}
          onChange={(event) => {
            onChange(event.target.value)
          }}
        />
      ) : (
        <CompactFormInput
          id={id}
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(event) => {
            onChange(event.target.value)
          }}
        />
      )}
    </CompactFormField>
  )
}

export function PrintColumnGroup({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xs font-semibold text-foreground">{title}</span>
        <span className="text-[11px] text-muted-foreground">（{hint}）</span>
      </div>
      <div className="flex flex-col gap-1 rounded-md bg-secondary/40 p-2">{children}</div>
    </div>
  )
}

export function PrintColumnCheckboxRow({
  label,
  checked,
  onToggle,
}: {
  label: string
  checked: boolean
  onToggle: () => void
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-secondary">
      <input type="checkbox" className="sr-only" checked={checked} onChange={onToggle} />
      <PrintCheckBox checked={checked} small />
      {label}
    </label>
  )
}
